import React from 'react';

const REELS = [
  {
    id: 'DWo2shaiCvK',
    author: 'Hamada Takifi | SVT 🧬',
    handle: '@moul.svt',
    url: 'https://www.instagram.com/reel/DWo2shaiCvK/',
  },
  {
    id: 'DVbuacUjYwS',
    author: 'ILYAS DAKKAOUI | Physique',
    handle: '@prof.7oroubat',
    url: 'https://www.instagram.com/reel/DVbuacUjYwS/',
  },
  {
    id: 'DUtN6xXCHkY',
    author: 'Prof Oussama Rahibe | Maths',
    handle: '@rahibe_maths',
    url: 'https://www.instagram.com/reel/DUtN6xXCHkY/',
  },
];

/**
 * ReelItem utilise un <iframe> direct vers l'URL d'embed Instagram.
 * Cette approche est beaucoup plus fiable que blockquote + embed.js :
 * - Fonctionne nativement sur mobile (iOS Safari, Android Chrome)
 * - Pas de dépendance au script Instagram externe
 * - Pas besoin de re-processing après les re-renders React
 * - React.memo évite les re-renders inutiles lors de la soumission du formulaire
 */
const ReelItem = React.memo(({ reel }: { reel: typeof REELS[0] }) => {
  return (
    <div className="relative w-full max-w-[400px] group">
      {/* Glow effect au hover */}
      <div className="absolute -inset-1 bg-gradient-to-br from-[#4fb7b3]/30 to-[#a8fbd3]/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm pointer-events-none" />

      <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black/40 backdrop-blur-xl shadow-2xl">
        {/* Header du reel */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white/5 border-b border-white/10">
          {/* Avatar placeholder */}
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#4fb7b3] to-[#a8fbd3] flex items-center justify-center flex-shrink-0">
            <span className="text-black font-black text-sm">P</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm truncate">{reel.author}</p>
            <p className="text-[#a8fbd3] text-xs font-mono">{reel.handle}</p>
          </div>
          {/* Logo Instagram */}
          <a
            href={reel.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-white/50 hover:text-white transition-colors"
            aria-label="Voir sur Instagram"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
            </svg>
          </a>
        </div>

        {/* Iframe embed direct — fonctionne sur mobile ET desktop */}
        <div className="relative w-full" style={{ paddingBottom: '177.78%' /* ratio 9:16 pour les reels */ }}>
          <iframe
            src={`https://www.instagram.com/reel/${reel.id}/embed/`}
            title={`Instagram Reel de ${reel.author}`}
            allowFullScreen
            scrolling="no"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
            }}
          />
        </div>

        {/* Footer avec lien */}
        <a
          href={reel.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border-t border-white/10 text-[#a8fbd3] hover:text-white hover:bg-white/10 transition-all text-sm font-mono tracking-wider"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
          </svg>
          Voir sur Instagram
        </a>
      </div>
    </div>
  );
});

ReelItem.displayName = 'ReelItem';

const InstagramReel: React.FC = () => {
  return (
    <div className="w-full py-10 relative z-20">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-start justify-items-center">
        {REELS.map((reel) => (
          <ReelItem key={reel.id} reel={reel} />
        ))}
      </div>
    </div>
  );
};

export default InstagramReel;
